// Imported explicitly, rather than relying on Nuxt's auto-import, so this pure function
// stays importable from a plain unit test — see useOrganizationServiceApi.ts for the same.
import { canAccessOrgManagement } from '~/composables/useClientManagementNav';

interface NavUserProfile {
  roles?: Record<string, string>;
  navigation?: { text: string }[];
}

/**
 * Exactly `/organizations` or a path under it — never a mere string prefix. A plain
 * `path.startsWith('/organizations')` also matches an unrelated route like
 * `/organizations-legacy`, letting it slip past every check below that exists to gate
 * this section off.
 */
const isOrgManagementPath = (path: string): boolean => path === '/organizations' || path.startsWith('/organizations/');

/**
 * The `admin.global.js` middleware's redirect decision, pure so it's testable without Nuxt
 * context — same reasoning as `canAccessOrgManagement` in useClientManagementNav.ts, which
 * this defers to rather than restating. Returns the path to redirect to, or null to leave
 * the user where they are.
 */
export const adminRedirectFor = (
  path: string,
  userProfile: NavUserProfile | null | undefined,
  flags?: Record<string, unknown> | null,
): string | null => {
  if (!userProfile) return null;

  const role = userProfile?.roles?.user_management;
  const isAdminPath = path.startsWith('/admin');
  const isOrgPath = isOrgManagementPath(path);

  // Already past the login middleware — account-settings is always reachable.
  if (path.startsWith('/account-settings')) return null;

  // Organization Management: passing the grant/flag check needs no redirect. Failing it
  // falls through to the checks below — a role-less user is caught immediately after, and
  // anyone else lands wherever the remaining checks put them.
  if (isOrgPath && canAccessOrgManagement(userProfile, flags)) return null;

  // No user_management role at all: their only home is account-settings, from any path.
  if (!role) return '/account-settings';

  // Admin is its own tier: belongs on /admin, and nowhere else.
  if (role === 'admin') {
    return isAdminPath ? null : '/admin';
  }

  // Any other role: /admin and Organization Management (reached here only when the grant/
  // flag check above failed) are both off-limits — bounce to `/`. Every other route is
  // unguarded and needs no redirect.
  return isAdminPath || isOrgPath ? '/' : null;
};
